"use client";

import { createLogger } from "@/lib/observability/logger";
import loadPlacesLibrary from "../common/load-places-library";
import { formatAddress, type PlaceDetails } from "./place-details.shared";

const logger = createLogger("place_details");

/**
 * Get place ID from postal code using Google Places API
 * @param postalCode - The postal code to search for
 * @returns The place ID or null if not found
 */
export async function getPlaceIdFromPostalCode(
  postalCode: string,
): Promise<string | null> {
  if (!postalCode) return null;

  try {
    const { Place } = await loadPlacesLibrary();

    // Use Place Text Search to find the postal code
    const response = await Place.searchByText({
      textQuery: postalCode,
      fields: ["id"],
    });

    const id: string | null = response?.places?.[0]?.id ?? null;
    return id;
  } catch (err) {
    logger.logError("getPlaceIdFromPostalCode", err as Error);
    throw new Error("Could not retrieve place id");
  }
}

/**
 * Get detailed place information from Google Places API
 * @param placeId - The Google Place ID
 * @returns PlaceDetails object or null if not found
 */
export async function getPlaceDetails(
  placeId: string,
): Promise<PlaceDetails | null> {
  if (!placeId) return null;

  try {
    const { Place } = await loadPlacesLibrary();

    const place = new Place({ id: placeId });
    await place.fetchFields({
      fields: [
        "addressComponents",
        "location",
        "viewport",
        "displayName",
        "formattedAddress",
        "plusCode",
      ],
    });

    let city = "",
      state = "",
      country = "",
      postalCode = "",
      streetNumber = "",
      route = "",
      subpremise = "";

    const addressComponents = place.addressComponents ?? [];
    addressComponents.forEach(
      (component: google.maps.places.AddressComponent) => {
        const types: string[] = component.types ?? [];
        const longText = component.longText ?? null;
        const shortText = component.shortText ?? null;

        if (!longText || !shortText) {
          return;
        }

        if (types.includes("locality")) {
          city = longText;
        } else if (types.includes("sublocality")) {
          city = longText;
        } else if (types.includes("administrative_area_level_1")) {
          state = shortText || longText;
        } else if (types.includes("country")) {
          country = shortText || longText;
        } else if (types.includes("postal_code")) {
          postalCode = longText;
        } else if (types.includes("street_number")) {
          streetNumber = longText;
        } else if (types.includes("route")) {
          route = longText;
        } else if (types.includes("subpremise")) {
          subpremise = longText;
        }
      },
    );

    const plusCode = place.plusCode ?? null;
    const address1 = `${streetNumber} ${route}`.trim();
    const address2 = subpremise || null;

    const viewport = place.viewport ?? null;
    if (!viewport) {
      throw new Error("No geometry/viewport data available for this place");
    }

    const boundingBox = {
      northeast: {
        lat: viewport.getNorthEast().lat(),
        lng: viewport.getNorthEast().lng(),
      },
      southwest: {
        lat: viewport.getSouthWest().lat(),
        lng: viewport.getSouthWest().lng(),
      },
    };

    const loc = place.location ?? null;
    if (!loc) {
      throw new Error("No location data available for this place");
    }

    const center = {
      lat: loc.lat(),
      lng: loc.lng(),
    };

    const name = place.displayName ?? null;
    const formattedAddress =
      place.formattedAddress ??
      formatAddress(address1, address2, city, state, postalCode);

    return {
      address1,
      address2,
      boundingBox,
      center,
      city,
      country,
      formattedAddress,
      name,
      placeId,
      plusCodeGlobal: plusCode?.globalCode ?? null,
      postalCode,
      state,
    };
  } catch (error) {
    logger.logError("getPlaceDetails", error as Error);
    throw new Error("Failed to fetch place details");
  }
}

/**
 * Get Google Place object for advanced usage
 * @param placeId - The Google Place ID
 * @returns Google Place object with fetched fields
 */
export async function getPlace(
  placeId: string,
): Promise<google.maps.places.Place> {
  const { Place } = await loadPlacesLibrary();

  const place = new Place({ id: placeId });
  await place.fetchFields({
    fields: [
      "addressComponents",
      "location",
      "viewport",
      "displayName",
      "formattedAddress",
      "plusCode",
    ],
  });
  return place;
}
